import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { Dicon, Button } from 'edcom-ui';

import Error, { getPageNotFoundTitle } from '../../components/Error';

import content from '../../services/content';
import { CollectionsProvider } from '../../services/CollectionsContext';

import Head from '../../components/head';
import Breadcrumbs from '../../components/Breadcrumbs';
import AdminLinks from '../../components/AdminLinks/AdminLinks';
import CategoryTree from '../../components/CategoryTree/CategoryTree';
import RelatedGuidedLesson from '../../components/RelatedGuidedLessons';
import GuidedLessonAction from '../../components/game/GuidedLessonAction';
import GameIFrame, { openGameIFrame } from '../../components/game/GameIFrame';
import CollectionsBookmark from '../../components/Collections/CollectionsBookmark';

import styles from './game.module.less';
import AccountContext from '../../services/AccountContext';
import memberService from '../../services/memberApi';
import Credits from '../../components/Credits';
import { getProductLinkingData } from '../../components/LinkingData/util';

const AssignmentsModal = dynamic(
  () => import('../../components/AssignmentsModal'),
  {
    ssr: false,
  }
);

const RelatedLearningResources = dynamic(
  () => import('../../components/RelatedLearningResources'),
  { ssr: false }
);

const ShareOptions = dynamic(() => import('../../components/ShareOptions'), {
  ssr: false,
});

const Game = ({
  id,
  title,
  urlName,
  internalName,
  metaTitle = '',
  metaDescription = '',
  published = false,
  indexed = false,
  text = '',
  standards = [],
  grades = [],
  subjects = [],
  mainImage = null,
  guidedLesson = null,
  course = null,
  allowAnonPlay = false,
  type = 'game',
  error = false,
  description = '',
}) => {
  const [assignmentsIsOpen, setAssignmentsIsOpen] = useState(false);
  const [canDownload, setCanDownload] = useState(false);

  const handleMainAction = event => {
    event.preventDefault();
    openGameIFrame();
  };

  const { memberId, isPremium, isAdmin } = useContext(AccountContext);

  const contentTypeMap = {
    game: {
      path: 'games',
      breadcrumb: 'Games',
      title: 'Game',
    },
    song: {
      path: 'songs',
      breadcrumb: 'Songs',
      title: 'Song video',
    },
    story: {
      path: 'stories',
      breadcrumb: 'Stories',
      title: 'Interactive story',
    },
  };

  const hideUnpublished = !published && !isAdmin;

  if (
    hideUnpublished ||
    (error && error.statusCode && error.statusCode === 404)
  ) {
    if (!error) error = { statusCode: 404 }; // Return a 404 (Not Found) for non-admins if the content is unpublished
    metaTitle = getPageNotFoundTitle();
  }

  if (!mainImage)
    mainImage = `https://cdn.education.com/files/static/game-images/${urlName}.jpg`;

  const defaultLessonPath = !guidedLesson
    ? `singlePlay:${id},${subjects[0]?.internalName || ''},`
    : `${course.subject.toLowerCase()},${course.internalName},${
        guidedLesson.urlName
      },${internalName}`;

  useEffect(() => {
    memberService.checkCanDownload(type, id).then(resp => {
      setCanDownload(resp?.allowed === true);
    });
  }, [memberId, isPremium]);

  return (
    <>
      <Head
        description={metaDescription}
        index={indexed}
        image={mainImage}
        title={metaTitle}
        linkingData={getProductLinkingData(title, mainImage, description)}
      >
        <title>{metaTitle}</title>
        <meta property="og:type" content="educationdotcom:game" />
      </Head>
      <div
        className={clsx('page-width', 'typography-small', styles.gameDetail)}
      >
        {error ? (
          <Error err={error} />
        ) : (
          <CollectionsProvider>
            <Breadcrumbs
              className={styles.breadcrumbs}
              title={title}
              grades={grades}
              path={contentTypeMap[type].path}
              subjects={subjects}
              contentType={contentTypeMap[type].breadcrumb}
            />
            <div>
              <h3 className={styles.contentTitle}>
                <Dicon name={type} theme="dark" />{' '}
                <span className={styles.contentName}>
                  {contentTypeMap[type].title}
                </span>
              </h3>
              <h1>{title}</h1>
              {!!memberId && (
                <ShareOptions
                  name={title}
                  imageSrc={mainImage}
                  contentType={'game'}
                  contentId={id}
                  downloadHref={`/gc/game/${urlName}/`}
                  canDownload={canDownload}
                />
              )}
            </div>
            <div className={styles.content}>
              <div className={styles.contentInfo}>
                <div style={{ marginBottom: 15 }}>
                  {!isPremium ? (
                    <Credits refreshCredits={true} gap={11} />
                  ) : null}
                </div>
                <div className={styles.actionMain}>
                  <Button
                    size="normal"
                    onClick={handleMainAction}
                    href="#game-section"
                    target="_blank"
                  >
                    Play
                  </Button>
                  <Button
                    size="normal"
                    color="secondary"
                    className={'add-to-assignment'}
                    onClick={() => setAssignmentsIsOpen(true)}
                  >
                    Assign this Game
                  </Button>
                </div>
                <div className={styles.actionSecondary}>
                  <CollectionsBookmark
                    type={type}
                    id={id}
                    title={title}
                    thumbnail={mainImage}
                  />
                </div>

                <div dangerouslySetInnerHTML={{ __html: text }} />
                {guidedLesson && (
                  <GuidedLessonAction href={guidedLesson?.urlName} />
                )}
                <CategoryTree
                  resource={{
                    grades: grades,
                    subjects: subjects,
                    standards: standards,
                  }}
                />
              </div>
              <div className={styles.gameFrame}>
                <GameIFrame
                  defaultLessonPath={defaultLessonPath}
                  internalName={internalName}
                  imgSrc={mainImage}
                  alt={'Game'}
                  id={id}
                  title={title}
                  subject={
                    !course
                      ? subjects[0]?.urlName || ''
                      : course?.subject.toLowerCase()
                  }
                  grade={grades[0].urlName}
                  allowAnonPlay={allowAnonPlay}
                  inGuidedLesson={!!guidedLesson}
                  contentType={type}
                  canDownload={canDownload}
                />
              </div>
            </div>
            {!guidedLesson ||
            guidedLesson.contentCounts === undefined ? null : (
              <RelatedGuidedLesson {...guidedLesson} />
            )}
            <RelatedLearningResources type="game" id={id} />
          </CollectionsProvider>
        )}
      </div>
      <AssignmentsModal
        itemsToAdd={[{ type: 'exercise', internalName: id }]}
        onClose={() => setAssignmentsIsOpen(false)}
        isOpen={assignmentsIsOpen}
        section="Game detail"
        contentId={id}
        contentType={type}
      />
      <AdminLinks>
        <a
          href={`/secure/exercises/exercise.php?nocache&id=${id}`}
          target="_blank"
        >
          Edit Exercise
        </a>
        <div>
          <strong>Status: </strong>
          {published ? <span>Published</span> : <span>Unpublished</span>}
        </div>
      </AdminLinks>
    </>
  );
};

Game.getInitialProps = async ({ asPath, req, query, res }) => {
  const contentService = content(req);
  const { urlName } = query;

  try {
    const response = await contentService.getGameDetails(urlName);

    // Return a 404 (Not Found) for unpublished resources
    if (!response.published) res.statusCode = 404;

    // Footer props for Tracking
    response.footerProps = {
      grades: response?.grades
        ? response.grades.map(grade => grade.urlName)
        : [],
      subjects: response?.subjects
        ? response.subjects.map(subject => subject.internalName)
        : [],
      contentType: response?.type,
      pageType: 'Content',
    };

    return {
      ...response,
      pageKey: asPath,
    };
  } catch (error) {
    if (error.statusCode === 404) {
      res.statusCode = 404;
    }
    return { error };
  }
};

const categoryShape = PropTypes.shape({
  urlName: PropTypes.string,
  internalName: PropTypes.string,
  name: PropTypes.string,
});

Game.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  internalName: PropTypes.string.isRequired,
  metaTitle: PropTypes.string,
  description: PropTypes.string.isRequired,
  metaDescription: PropTypes.string,
  published: PropTypes.bool,
  indexed: PropTypes.bool,
  text: PropTypes.string.isRequired,
  standards: PropTypes.arrayOf(categoryShape),
  grades: PropTypes.arrayOf(categoryShape),
  subjects: PropTypes.arrayOf(categoryShape),
  type: PropTypes.string,
  mainImage: PropTypes.string,
  guidedLesson: PropTypes.object,
  course: PropTypes.object,
  allowAnonPlay: PropTypes.bool,
  error: PropTypes.object,
};

export default Game;
